import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Settings, Save, User, Terminal, FlaskConical, Trash2, Cpu } from "lucide-react";
import { useUserProfile } from "@/hooks/useUserProfile";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { generateTestMap, deleteAllTestMaps } from "@/services/testDataGenerator";
import { useWorkerConfig } from "@/hooks/useWorkerConfig";

export default function SettingsPage() {
  const { user } = useAuth();
  const { displayName, loading, saveDisplayName } = useUserProfile();
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [debugMode, setDebugMode] = useState(false);
  const [hideFab, setHideFab] = useState(false);
  const [generatingTest, setGeneratingTest] = useState(false);
  const [deletingTest, setDeletingTest] = useState(false);
  const {
    useWorkerMode,
    useWorkerModeMap,
    loading: workerLoading,
    saveUseWorkerMode,
    saveUseWorkerModeMap,
  } = useWorkerConfig();

  useEffect(() => {
    setName(displayName);
  }, [displayName]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    setDebugMode(localStorage.getItem("app_log_level") === "debug");
    setHideFab(localStorage.getItem("app_logs_fab_hidden") === "true");
  }, []);

  const toggleDebugMode = (val: boolean) => {
    setDebugMode(val);
    if (val) localStorage.setItem("app_log_level", "debug");
    else localStorage.removeItem("app_log_level");
    // Trigger storage event for same-tab listeners (contentLogger, FAB)
    window.dispatchEvent(new StorageEvent("storage", { key: "app_log_level" }));
    toast({
      title: val ? "Tryb debug włączony" : "Tryb debug wyłączony",
      description: val
        ? "Wszystkie logi DEBUG są teraz zapisywane do IndexedDB."
        : "Tylko PUBLIC/ERROR/WARN są zapisywane.",
    });
  };

  const toggleHideFab = (val: boolean) => {
    setHideFab(val);
    if (val) localStorage.setItem("app_logs_fab_hidden", "true");
    else localStorage.removeItem("app_logs_fab_hidden");
    window.dispatchEvent(new StorageEvent("storage", { key: "app_logs_fab_hidden" }));
  };

  const handleGenerateTest = async () => {
    setGeneratingTest(true);
    try {
      const result = await generateTestMap();
      toast({
        title: "Mapa testowa utworzona",
        description: `${result.mapName} — ${result.seedCount} seedów, ${result.nodeCount} nodów, ${result.entityCount} encji`,
      });
    } catch (err) {
      toast({
        title: "Błąd",
        description: err instanceof Error ? err.message : "Nieznany błąd",
        variant: "destructive",
      });
    } finally {
      setGeneratingTest(false);
    }
  };

  const handleDeleteTest = async () => {
    if (!confirm("Usunąć WSZYSTKIE mapy z prefixem TEST_? Operacja nieodwracalna.")) return;
    setDeletingTest(true);
    try {
      const count = await deleteAllTestMaps();
      toast({
        title: "Map testowych usunięto",
        description: `Liczba: ${count}`,
      });
    } catch (err) {
      toast({
        title: "Błąd",
        description: err instanceof Error ? err.message : "Nieznany błąd",
        variant: "destructive",
      });
    } finally {
      setDeletingTest(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast({ title: "Blad", description: "Nazwa nie moze byc pusta", variant: "destructive" });
      return;
    }
    setSaving(true);
    const { error } = await saveDisplayName(name.trim());
    if (error) {
      toast({ title: "Blad", description: error, variant: "destructive" });
    } else {
      toast({ title: "Zapisano", description: "Nazwa wyswietlana zostala zaktualizowana." });
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-muted rounded w-1/4" />
        <div className="h-40 bg-muted rounded" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Settings className="h-6 w-6" />
        <h1 className="text-2xl font-bold">Ustawienia</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <User className="h-4 w-4" />
            Profil uzytkownika
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" value={user?.email || ""} disabled className="bg-muted" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="display-name">Nazwa wyswietlana</Label>
            <Input
              id="display-name"
              placeholder="np. Jan, Darek, Admin..."
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSave();
              }}
              maxLength={50}
            />
            <p className="text-xs text-muted-foreground">
              Ta nazwa bedzie wyswietlana w komentarzach w Taskach i Pomyslach zamiast "Ty" / "Uzytkownik".
            </p>
          </div>

          <Button onClick={handleSave} disabled={saving || !name.trim()}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Zapisywanie..." : "Zapisz"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Terminal className="h-4 w-4" />
            Logi aplikacji
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <Label htmlFor="debug-mode" className="cursor-pointer">
                Tryb deweloperski (debug logs)
              </Label>
              <p className="text-xs text-muted-foreground max-w-md">
                Włącza zapis szczegółowych logów DEBUG (modele LLM, tokeny, payloady).
                Działa runtime — nie wymaga restartu. Domyślnie tylko PUBLIC/ERROR/WARN są
                zapisywane.
              </p>
            </div>
            <Switch
              id="debug-mode"
              checked={debugMode}
              onCheckedChange={toggleDebugMode}
            />
          </div>

          <div className="flex items-start justify-between gap-4 pt-3 border-t">
            <div className="space-y-1">
              <Label htmlFor="hide-fab" className="cursor-pointer">
                Ukryj floating button "Logi"
              </Label>
              <p className="text-xs text-muted-foreground max-w-md">
                Domyślnie w prawym dolnym rogu widoczny jest okrągły przycisk z dostępem
                do logów aplikacji. Możesz go ukryć — logi nadal są zapisywane w tle,
                ale dostępne tylko przez ten ekran.
              </p>
            </div>
            <Switch id="hide-fab" checked={hideFab} onCheckedChange={toggleHideFab} />
          </div>
        </CardContent>
      </Card>

      {/* Worker Mode card — ukryty (worker stub, browser-mode jest primary path).
          Odkomentuj jak zaczniemy uzywac workera ponownie. */}
      {debugMode && (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Cpu className="h-4 w-4" />
            Worker Mode (background queue)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Stub-mode ostrzezenie — pokazywane ZAWSZE (zniknie po pelnej integracji) */}
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs space-y-1">
            <p className="font-semibold text-amber-700 dark:text-amber-400">
              ⚠ Worker dziala w trybie STUB — pelna integracja w przygotowaniu
            </p>
            <p className="text-muted-foreground">
              Po wlaczeniu tego toggla kolejka leci do kontenera workera (Redis + BullMQ),
              ale <strong>procesory workera obecnie tylko wstawiaja rows do queue</strong>.
              Faktyczne generowanie artykulow nadal wykonuje browser mode (musisz miec
              otwarta karte). Full reuse src/services/contentEngine wymaga Node-side refactoru
              i zostanie dokonczony w osobnej sesji.
            </p>
            <p className="text-muted-foreground">
              Szczegoly: <code className="font-mono">NOWADOKUMENTACJA/23-WORKER-MODE.md</code>
            </p>
          </div>
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <Label htmlFor="worker-mode" className="cursor-pointer">
                Uzyj workera (browser-independent)
              </Label>
              <p className="text-xs text-muted-foreground max-w-md">
                Gdy wlaczone: generowanie i publikacja artykulow beda wykonywane przez
                serwer workerowy 24/7 — mozesz zamknac przegladarke. Wymaga deployu
                kontenera workera w Coolify oraz skonfigurowanego Redis.
                Domyslnie wylaczone (browser mode — kolejki potrzebuja otwartej karty).
              </p>
            </div>
            <Switch
              id="worker-mode"
              checked={useWorkerMode}
              disabled={workerLoading}
              onCheckedChange={(v) => saveUseWorkerMode(v)}
            />
          </div>

          {/* Worker Mode dla MAPY (seedy/nody) — osobna flaga */}
          <div className="flex items-start justify-between gap-4 pt-4 border-t border-border">
            <div className="space-y-1">
              <Label htmlFor="worker-mode-map" className="text-sm font-medium">
                Worker Mode dla mapy (seedy + nody)
              </Label>
              <p className="text-xs text-muted-foreground max-w-md">
                Gdy wlaczone: generowanie seedow i nodow leci przez worker container
                zamiast przegladarki. Mozesz zamknac karte po kliknieciu "Generuj Seeds" —
                drafty pojawia sie w DB automatycznie gdy skonczy. Wymaga tego samego
                workera co CE + dodatkowo `ANTHROPIC_API_KEY` w Coolify env.
                Domyslnie wylaczone (browser mode).
              </p>
            </div>
            <Switch
              id="worker-mode-map"
              checked={useWorkerModeMap}
              disabled={workerLoading}
              onCheckedChange={(v) => saveUseWorkerModeMap(v)}
            />
          </div>
        </CardContent>
      </Card>
      )}

      {debugMode && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <FlaskConical className="h-4 w-4 text-amber-500" />
              Dane testowe (tylko dev mode)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground max-w-2xl">
                Generuje testową mapę z prefixem <code className="font-mono text-amber-500">TEST_</code> zawierającą:
                2 seedy × 3 nody, encje, EAV, relacje. Pomaga przejść przez cały pipeline UX
                bez czekania na realne LLM calls.
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <Button
                onClick={handleGenerateTest}
                disabled={generatingTest}
                variant="outline"
                className="border-amber-500/40 text-amber-600 dark:text-amber-400 hover:bg-amber-500/10"
              >
                <FlaskConical className="h-4 w-4 mr-2" />
                {generatingTest ? "Generuję..." : "Generuj testową mapę"}
              </Button>
              <Button
                onClick={handleDeleteTest}
                disabled={deletingTest}
                variant="ghost"
                className="text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {deletingTest ? "Usuwam..." : "Usuń wszystkie TEST_*"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
